import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, Calendar, User, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InsightDetailPage() {
  const { id } = useParams();
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/insights/${id}`).then(res => { setInsight(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  const insightTitle = insight ? (insight.meta_title || (insight.title + " | Equaliser Legal LLP")) : "Loading... | Equaliser Legal LLP";
  const insightDesc = insight ? (insight.meta_description || insight.excerpt || insight.title) : "";
  useSEO(insightTitle, insightDesc);

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-20"><p className="text-slate-400">Loading...</p></div>;
  if (!insight) return <div className="min-h-screen flex flex-col items-center justify-center pt-20"><p className="text-slate-400 mb-4">Insight not found.</p><Link to="/insights" className="text-[#C5A059] hover:underline text-sm">Back to Insights</Link></div>;

  return (
    <div data-testid="insight-detail-page">
      <section className="bg-[#0A1628] noise-overlay relative pt-28 pb-16 md:pt-36 md:pb-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/insights" data-testid="back-to-insights" className="inline-flex items-center text-white/50 hover:text-[#C5A059] transition-colors text-sm mb-8"><ArrowLeft size={14} className="mr-1" /> Back to Insights</Link>
          <div className="flex items-center gap-3 mb-6">
            <span className="text-xs uppercase tracking-wider text-[#C5A059]">{insight.category}</span>
            <span className="text-white/30">|</span>
            <span className="flex items-center gap-1 text-white/40 text-xs"><Calendar size={12} /> {insight.published_date}</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-medium leading-tight tracking-tight">{insight.title}</h1>
          <div className="flex items-center gap-4 mt-6"><span className="flex items-center gap-1.5 text-white/50 text-sm"><User size={14} /> {insight.author}</span></div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {insight.thumbnail_url && (
            <div className="mb-10 rounded-[2px] overflow-hidden"><img src={insight.thumbnail_url} alt={insight.title} className="w-full h-64 object-cover" /></div>
          )}
          <div className="flex flex-wrap gap-2 mb-10">{(insight.tags || []).map((tag, i) => <span key={i} className="text-xs border border-slate-200 text-slate-500 px-3 py-1 rounded-[2px]">{tag}</span>)}</div>
          {insight.excerpt && <p className="text-slate-600 text-lg leading-relaxed mb-10 border-l-2 border-[#C5A059] pl-6 italic">{insight.excerpt}</p>}
          <div className="insight-content" dangerouslySetInnerHTML={{ __html: insight.content }} />
          
          {/* Documents for download */}
          {insight.documents && insight.documents.length > 0 && (
            <div className="mt-10 border-t border-slate-200 pt-6">
              <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-4">Downloads</h3>
              <div className="flex flex-wrap gap-3">
                {insight.documents.map((doc, i) => (
                  <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer" data-testid={`download-doc-${i}`} className="inline-flex items-center gap-2 border border-[#0A1628] text-[#0A1628] px-5 py-2.5 text-sm hover:bg-[#0A1628] hover:text-white transition-colors duration-300 rounded-[2px]">
                    <Download size={14} /> {doc.name}
                  </a>
                ))}
              </div>
            </div>
          )}

          {insight.pdf_url && (
            <a href={insight.pdf_url} target="_blank" rel="noopener noreferrer" data-testid="download-pdf" className="inline-flex items-center gap-2 mt-8 border border-[#0A1628] text-[#0A1628] px-6 py-3 text-sm hover:bg-[#0A1628] hover:text-white transition-colors duration-300 rounded-[2px]"><Download size={16} /> Download PDF</a>
          )}

          <div className="mt-16 border-t border-slate-200 pt-8"><p className="text-slate-400 text-xs leading-relaxed italic">This material is for general information only and does not constitute legal advice or a solicitation. Specific advice should be obtained before taking or refraining from any action.</p></div>
        </div>
      </section>
    </div>
  );
}
